<template>
  <div class="page-operative-container flex-grow-1 d-flex flex-row align-items-stretch pt-0">
    <div class="flex-grow-1 p-3" v-if="!company || company.disabled">
      <div class="container d-flex align-items-center h-100">
        <div class="alert alert-primary shadow-lg">
          <h2 class="alert-heading h4 mb-4 ">Questa azienda è disabilitata</h2>
          <p>Siamo spiacenti di informarla che questa azienda è stata disabilitata. Contatti il suo supervisor per procedere all'attivazione di RFF.</p>
          <hr>
          <router-link class="btn btn-outline-primary" :to="{ name: 'companies' }">
            <i class="fas fa-chevron-left mr-2"></i> Torna alle aziende
          </router-link>
        </div>
      </div>
    </div>
    <template v-else-if="hasBeenSetup">
      <transition name="slide">
        <AwlSidenav class="sidenav shadow-lg d-md-block" :links="links" v-show="$open" />
      </transition>
      <router-view :company="company" class="page-operative flex-grow-1 p-3" />
    </template>
    <Setup class="flex-grow-1 p-3" v-else-if="userIsAdmin || userIsContextCompanyOwner || userIsContextCompanyConsultant" :company="company" @finalizeSetup="finalizeSetup" />
    <div class="flex-grow-1 p-3" v-else>
      Questa azienda richiede ancora il setup.
    </div>
  </div>
</template>

<script>

import $oauth2 from '@/libs/OAuth2/services/auth.js';
import $api from '@/libs/Api/services/api.js';
import sidebarMixin from '@/libs/Sidebar/mixins/sidebar.js';
import operativeContextRolesMixin from '@/mixins/operativeContextRoles.js';
import { companyHasModule } from '@/helpers/modules.js';

export default {
  mixins: [sidebarMixin, operativeContextRolesMixin],
  components: {
    AwlSidenav: () => import('@/views/components/Layout/LayoutOperative/AwlSidenav.vue'),
    Setup: () => import('./Setup/Setup.vue'),
  },
  data () {
    return {
      company: null,
    };
  },
  computed: {
    hasBeenSetup () {
      return this.company?.hasBeenSetup;
    },
    links () {
      return [
        {
          name: 'Dashboard',
          to: { name: 'operative-company' },
          icon: 'far fa-newspaper',
          appLink: true,
        },
        {
          name: 'Progetti',
          icon: 'fas fa-project-diagram',
          hide: (!this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner && !this.userIsContextCompanyManager) || !companyHasModule(this.company, 'PROJECTS'),
          children: [
            {
              name: 'Dashboard',
              to: { name: 'operative-company.projects.dashboard' },
              appLink: true,
              hide: true,
            },
            {
              name: 'Nuovo progetto',
              to: { name: 'operative-company.projects.add' },
              appLink: true,
            },
            {
              name: 'Lista progetti',
              to: { name: 'operative-company.projects.list' },
              appLink: true,
            },
            {
              name: 'Libreria: Costi figurati',
              to: { name: 'operative-company.projects.library.imputedCosts.list' },
              appLink: true,
            },
            {
              name: 'Libreria: Voci di costo',
              to: { name: 'operative-company.projects.library.expenses.list' },
              appLink: true,
            },
            {
              name: 'Libreria: Modelli di progetto',
              to: { name: 'operative-company.projects.library.templates.list' },
              appLink: true,
            },
          ],
        },
        {
          name: 'Analisi dati aziendali',
          icon: 'fas fa-chart-line',
          hide: (!this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner && !this.userIsContextCompanyManager),
          children: [
            {
              name: 'Ricavi',
              to: { name: 'operative-company.data-analysis.proceeds' },
              appLink: true,
            },
            {
              name: 'Costi proporzionali',
              to: { name: 'operative-company.data-analysis.cogs' },
              appLink: true,
            },
            {
              name: 'Costi di struttura',
              to: { name: 'operative-company.data-analysis.ga' },
              appLink: true,
            },
            {
              name: 'Costi del personale',
              to: { name: 'operative-company.data-analysis.personnel' },
              appLink: true,
            },
            {
              name: 'Investimenti',
              to: { name: 'operative-company.data-analysis.investimenti' },
              appLink: true,
            },
            {
              name: 'Finanziamenti',
              to: { name: 'operative-company.data-analysis.finanziamenti' },
              appLink: true,
            },
            {
              name: 'Equity',
              to: { name: 'operative-company.data-analysis.equity' },
              appLink: true,
            },
            {
              name: 'Contributi a fondo perduto',
              to: { name: 'operative-company.data-analysis.contributi-fondo-perduto' },
              appLink: true,
              hide: !companyHasModule(this.company, 'SUBSIDIZED_FINANCE'),
            },
            {
              name: 'Crediti d\'imposta',
              to: { name: 'operative-company.data-analysis.crediti-imposta' },
              appLink: true,
              hide: !companyHasModule(this.company, 'SUBSIDIZED_FINANCE'),
            },
            {
              name: 'Stati Patrimoniali a consuntivo',
              to: { name: 'operative-company.data-analysis.stati-patrimoniali' },
              appLink: true,
            },
            {
              name: 'Scadenzari',
              to: { name: 'operative-company.data-analysis.scadenzari' },
              appLink: true,
              hide: true,
            },
          ],
        },
        {
          name: 'Dati fiscali',
          icon: 'fas fa-file-invoice-dollar', // rff-icon icon-fiscal-data
          hide: (!this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner && !this.userIsContextCompanyManager),
          children: [
            {
              name: 'Dinamica IVA',
              to: { name: 'operative-company.fiscal-data.dinamica-iva' },
              appLink: true,
            },
            {
              name: 'Credito d\'imposta',
              to: { name: 'operative-company.fiscal-data.credito-imposta' },
              appLink: true,
              hide: !companyHasModule(this.company, 'SUBSIDIZED_FINANCE'),
            },
            {
              name: 'Gestione fiscale',
              to: { name: 'operative-company.fiscal-data.gestione-fiscale' },
              appLink: true,
            },
          ],
        },
        {
          name: 'Bilancio',
          icon: 'fas fa-coins', // rff-icon icon-balance
          children: [
            {
              name: 'Analisi reddituale',
              to: { name: 'operative-company.balance.income-statement' },
              appLink: true,
            },
            {
              name: 'Cash flow',
              to: { name: 'operative-company.balance.cash-flow' },
              appLink: true,
            },
            {
              name: 'Stato patrimoniale finanziario',
              to: { name: 'operative-company.balance.balance' },
              appLink: true,
            },
          ],
        },
        {
          name: 'Prevenzione crisi d\'impresa',
          to: { name: 'operative-company.crisi-impresa' },
          icon: 'fas fa-exclamation',
          hide: !this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner && !this.userIsContextCompanyVisitor && !this.userIsContextCompanyManager,
          appLink: true,
        },
        {
          name: 'KPI',
          to: { name: 'operative-company.kpi' },
          icon: 'fas fa-tachometer-alt', // rff-icon icon-kpi
          appLink: true,
        },
        {
          name: 'Impostazioni',
          to: { name: 'operative-company.settings' },
          icon: 'fas fa-tools', // rff-icon icon-settings
          hide: !this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner,
          appLink: true,
        },
        {
          name: 'Investitori',
          to: { name: 'operative-company.investors' },
          icon: 'fas fa-hand-holding-usd',
          hide: (!this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner) || !companyHasModule(this.company, 'INVESTORS'),
          appLink: true,
        },
        {
          name: 'Forecasts',
          to: { name: 'operative-company.forecast' },
          icon: 'fas fa-hat-wizard',
          hide: !this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner && !this.userIsContextCompanyManager,
          appLink: true,
        },
        {
          name: 'Utenti',
          to: { name: 'operative-company.users' },
          icon: 'fas fa-users',
          hide: (!this.userIsAdmin && !this.userIsContextCompanyConsultant && !this.userIsContextCompanyOwner) || !companyHasModule(this.company, 'USERS'),
          appLink: true,
        },
      ].filter(l => !l.hide);
    },
    isInvestor () {
      return this.$oauth2?.me?.investor;
    },
  },
  methods: {
    finalizeSetup (setup) {
      this.$api.activateCompany(this.$route.params.id, setup.year, setup.month, setup.balance ? setup.balance : null).then(res => {
        this.company = res.data || null;
      }, this.$log.error);
    },
  },
  async beforeRouteEnter (to, from, next) {
    try {
      const res = await $api.company(to.params.id);
      const company = res?.data || null;
      const companyId = company?.id || null;
      const forecast = res.data?.forecast || null;

      const userCompany = $oauth2?.me?.investor || $oauth2?.me?.admin || ($oauth2?.me?.companies || []).find(c => c.id === companyId);

      if (!userCompany) {
        return next(from);
      }

      return next(vm => {
        vm.company = company;
        vm.$appState.context = vm.company;
        vm.$appState.forecast = forecast || null;
      });
    } catch (error) {
      return next(vm => vm.$log.error(error));
    }
  },
  async beforeRouteUpdate (to, from, next) {
    try {
      const res = await $api.company(to.params.id);

      const company = res?.data || null;
      const companyId = company?.id || null;

      const userCompany = $oauth2?.me?.investor || $oauth2?.me?.admin || ($oauth2?.me?.companies || []).find(c => c.id === companyId);

      if (!userCompany) {
        return next({ name: 'root' });
      }

      this.company = res.data || null;
      const forecast = res.data?.forecast || null;
      this.$appState.context = this.company;
      this.$appState.forecast = forecast;
    } catch (error) {
      this.$log.error(error);

      return next({ name: 'root' });
    }

    next();
  },
};

</script>

<style lang="scss">

.page-operative-container {
  overflow: hidden;
  position: relative;
}
@media print {
  .page-operative-container {
    overflow: visible;
    height: 100%;
    width: 100%;
  }
}

@include media-breakpoint-down("sm") {
  .slide-enter, .slide-leave-to {
    left: -100%;
  }
  .slide-enter-to, .slide-leave {
    left: 0;
  }

  .slide-enter-active,.slide-leave-active {
    transition-duration: .4s;
  }
}

.sidenav {
  width: 82%;
  z-index: 998;

  @include media-breakpoint-down("sm") {
    height: 100vh;
    top: 0;
    position: fixed;
  }

  @include media-breakpoint-up("md") {
    width: 270px;
    z-index: 99;
  }
}

.report-texts-size {
  font-size: 12px;

  .operative-table, &.operative-table {
    td, th {
      padding: 0.1rem;
    }
  }

  &.awl-table .table {
    td, th {
      padding: 0.1rem !important;
    }
  }
}

</style>
